import './SeatAction.scss';

import * as React from 'react';

import {
  SlDropdown,
  SlMenu,
  SlSkeleton,
} from '@shoelace-style/shoelace/dist/react';
import { TeamAccessType, TeamRole } from '@showbie-socrative/socrative-utils';
import { useAtomValue, useSetAtom } from 'jotai';

import { sendTeacherAmplitudeEvent } from '~analytics/amplitude';
import { sendTeacherIntercomEvent } from '~analytics/intercom';
import { EventTypes } from '~analytics/types';
import { translate } from '~l10n/client';
import { FocusIndicator } from '~shared/components/FocusIndicator';
import { IconChevron } from '~shared/components/icons/micro';
import { toast } from '~shared/components/Toast';
import { selectedTeamIdAtom } from '~teacher/components/Account/atoms';
import { useSelectedTeamQuery } from '~teacher/components/Account/hooks';
import { selectedPublicIdsAtom } from '~teacher/components/Account/TeamMembers/TeamMembersTable/atoms';
import { isUpgradeMemberDialogOpenAtom } from '~teacher/components/Account/TeamMembers/TeamMembersTable/UpgradeMemberDialog';
import { useSelectedTeamHasActiveSubscriptionQuery } from '~teacher/components/PricingTable/hooks/useHasTeamActiveSubscriptionQuery';
import { useTeams } from '~teacher/hook/team';
import { useChangeTeamAccessLevel } from '~teacher/hook/team/useChangeTeamAccessLevel';

interface SeatActionMenuProps {
  publicId: string;
  accessLevel: TeamAccessType;
}

const accessLevelSeatMap: Record<TeamAccessType, string> = {
  paid: translate('Paid') as string,
  free: translate('Free') as string,
};

export function SeatAction({ accessLevel, publicId }: SeatActionMenuProps) {
  const selectedTeamId = useAtomValue(selectedTeamIdAtom);

  const hideActionsQuery = useTeams({
    select: (teams) =>
      teams.find((team) => team.id === selectedTeamId)?.role ===
      TeamRole.Member,
  });

  const doesTeamHaveActiveSubscriptionQuery =
    useSelectedTeamHasActiveSubscriptionQuery();

  if (
    hideActionsQuery.data === undefined ||
    doesTeamHaveActiveSubscriptionQuery.data === undefined
  ) {
    return <SlSkeleton className="w-20" />;
  }

  if (
    hideActionsQuery.data ||
    doesTeamHaveActiveSubscriptionQuery.data === false
  ) {
    return (
      <span className="capitalize">{accessLevelSeatMap[accessLevel]}</span>
    );
  }

  return <SeatActionMenu publicId={publicId} accessLevel={accessLevel} />;
}

function SeatActionMenu({
  publicId,
  accessLevel,
}: {
  publicId: string;
  accessLevel: TeamAccessType;
}) {
  return (
    <SlDropdown>
      <button
        slot="trigger"
        className="group relative grid items-center border-0 rounded-2 bg-transparent pl-0 appearance-none  content-center gap-1 h-full seat-action-menu cursor-pointer focus:outline-none"
      >
        <span className="capitalize font-sans font-sans text-base text-blue-900">
          {accessLevelSeatMap[accessLevel]}
        </span>
        <IconChevron direction="down" />
        <FocusIndicator />
      </button>
      <SlMenu className="action-menu ">
        <DowngradeActionItem publicId={publicId} accessLevel={accessLevel} />
        <UpgradeActionItem publicId={publicId} accessLevel={accessLevel} />
      </SlMenu>
    </SlDropdown>
  );
}

function UpgradeActionItem({
  publicId,
  accessLevel,
}: {
  publicId: string;
  accessLevel: TeamAccessType;
}) {
  const selectedTeam = useSelectedTeamQuery();
  const replaceSelectedPublicId = useSetAtom(selectedPublicIdsAtom.replaceAtom);
  const setIsUpgradeMemberDialogOpen = useSetAtom(
    isUpgradeMemberDialogOpenAtom
  );

  if (!selectedTeam.data) {
    return null;
  }

  if (selectedTeam.data.role === TeamRole.Member) {
    return null;
  }

  if (accessLevel === TeamAccessType.Paid) {
    return null;
  }

  const onClick = () => {
    replaceSelectedPublicId(new Set([publicId]));
    setIsUpgradeMemberDialogOpen(true);
  };

  return (
    <SeatMenuItem onClick={onClick}>
      {translate('Upgrade to Paid')}
    </SeatMenuItem>
  );
}

function DowngradeActionItem({
  publicId,
  accessLevel,
}: {
  publicId: string;
  accessLevel: TeamAccessType;
}) {
  const selectedTeamId = useAtomValue(selectedTeamIdAtom);

  const selectedTeam = useSelectedTeamQuery();
  const changeAccessLevelMutation = useChangeTeamAccessLevel();

  const onDowngradeMember = () => {
    changeAccessLevelMutation.mutate(
      {
        teamId: selectedTeamId,
        memberPublicId: publicId,
        newAccessType: TeamAccessType.Free,
      },
      {
        onSuccess: () => {
          toast(translate('Done'));

          sendTeacherAmplitudeEvent(EventTypes.TeamsChangeSeat, {
            team_id: selectedTeamId,
            new_seat: TeamAccessType.Free,
            previous_seat: TeamAccessType.Paid,
          });
          sendTeacherIntercomEvent(EventTypes.TeamsChangeSeat);
        },
        onError: () => {
          toast.critical(translate('An error has occurred'));
        },
        onSettled: () => {},
      }
    );
  };

  if (!selectedTeam.data) {
    return null;
  }

  if (selectedTeam.data.role === TeamRole.Member) {
    return null;
  }

  if (accessLevel === TeamAccessType.Free) {
    return null;
  }

  return (
    <SeatMenuItem onClick={onDowngradeMember}>
      {translate('Downgrade to Free')}
    </SeatMenuItem>
  );
}

/**
 * This component is very similar to the `SLMenuItem` component. It just lacks all the
 * pieces that were not being used.
 * @param onClick
 * @param children
 */
function SeatMenuItem({
  onClick,
  children,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className="action-item py-1 px-2 min-w-40 max-w-92 w-fit"
      role="menuitem"
      tabIndex={0}
    >
      <div className="seat-menu-item hover:bg-cyan-50 py-2 pl-6 pr-7 rounded-1 relative flex justify-center items-stretch font-sans text-base leading-8 cursor-pointer select-none">
        {children}
      </div>
    </div>
  );
}
